/**
 * Where administration records live, in one place.
 *
 * Admin details used to sit at top-level URLs - `/admin/users/48`,
 * `/admin/roles/static_19` - which meant clicking a row in a settings section
 * navigated OUT of settings: the rail vanished, and the breadcrumb changed
 * vocabulary mid-journey ("Admin › Roles" from one entry point, "Settings ›
 * Users" from another). A record reached from a section is a sub-view of that
 * section, so its URL is a child of the section's.
 *
 * Pure strings and pure functions: the section components navigate with them,
 * `PrivateRoutes` mounts and redirects with them, and the tests pin the
 * mapping - so the three cannot drift.
 */

export const ADMIN_USERS_SECTION_PATH = '/settings/admin-users';
export const ADMIN_ROLES_SECTION_PATH = '/settings/admin-roles';

/** The settings route that carries a record id, e.g. `/settings/admin-users/48`. */
export const SETTINGS_DETAIL_ROUTE = '/settings/:sectionId/:detailId';

export const adminUserPath = (id: string | number): string =>
  `${ADMIN_USERS_SECTION_PATH}/${encodeURIComponent(String(id))}`;

export const adminRolePath = (id: string | number): string =>
  `${ADMIN_ROLES_SECTION_PATH}/${encodeURIComponent(String(id))}`;

/** The pre-settings URLs, still linked from old bookmarks and emails. */
export const LEGACY_ADMIN_USER_ROUTE = '/admin/users/:userId';
export const LEGACY_ADMIN_ROLE_ROUTE = '/admin/roles/:roleId';

/**
 * Where a legacy admin detail URL should land, or null when it is not one.
 *
 * `/admin/roles/create` is deliberately NOT a detail - it is the create form,
 * and redirecting it would send "new role" to a role named "create".
 */
export const resolveLegacyAdminDetail = (pathname: string): string | null => {
  const path = (pathname || '').replace(/\/+$/, '');

  const user = path.match(/^\/admin\/users\/([^/]+)$/);
  if (user) return adminUserPath(decodeURIComponent(user[1]));

  const role = path.match(/^\/admin\/roles\/([^/]+)$/);
  if (role && role[1] !== 'create') return adminRolePath(decodeURIComponent(role[1]));

  return null;
};
